/**
 * Tells someone when the site has been updated underneath them.
 *
 * A single-page app fetches index.html once. After that it swaps views in the browser and never
 * asks the server for the HTML again — so a tab left open keeps running the JavaScript it
 * downloaded on day one, however many times we deploy. That is not theoretical: a routing fix
 * shipped and the person testing it kept seeing the old behaviour for an hour, because their tab
 * was still executing the previous build. Only a private window (no open tab) picked it up.
 *
 * How it works: the running page knows its own bundle filename, which carries a content hash.
 * We re-fetch index.html — cache-busted, since the browser holds a copy — and read the filename
 * the server is serving now. Different filename means a new build is live.
 *
 * Deliberate choices:
 *   - It never reloads on its own. Someone half-way through typing an order would lose it.
 *   - It stays quiet on checkout and payment pages, where an interruption costs a sale.
 *   - Dismissing it holds it back for an hour, rather than asking again on the next tick.
 *   - Any network failure is silence. A flaky connection must not produce a banner.
 */
import { useEffect, useState } from 'react'

const CHECK_INTERVAL_MS = 5 * 60 * 1000     // every five minutes while the tab is open
const SNOOZE_MS = 60 * 60 * 1000            // "not now" means an hour, not five minutes
const SNOOZE_KEY = 'version_prompt_snoozed_until'

/** Paths where a banner would interrupt something that matters. */
const QUIET_PATHS = ['/checkout', '/payment', '/cart', '/complete-profile', '/sign-contract',
                     '/direct-debit', '/verify']

/** The bundle this page is actually running, taken from its own script tag. */
function runningBuild(): string | null {
  const el = document.querySelector('script[type="module"][src*="/assets/index-"]')
  const src = el?.getAttribute('src') || ''
  const m = src.match(/\/assets\/(index-[A-Za-z0-9_-]+\.js)/)
  return m ? m[1] : null
}

/** The bundle the server is serving right now. Null if we could not tell. */
async function deployedBuild(): Promise<string | null> {
  try {
    // Cache-busted and no-store: we specifically need to bypass the copy the browser holds.
    const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const html = await res.text()
    const m = html.match(/\/assets\/(index-[A-Za-z0-9_-]+\.js)/)
    return m ? m[1] : null
  } catch {
    return null                              // offline, or the check itself failed — say nothing
  }
}

function snoozed(): boolean {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0)
    return Number.isFinite(until) && Date.now() < until
  } catch {
    return false
  }
}

export default function NewVersionPrompt() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const mine = runningBuild()
    // In dev there is no hashed bundle to compare against, so there is nothing to do.
    if (!mine) return

    let stopped = false

    const check = async () => {
      if (stopped || snoozed()) return
      if (QUIET_PATHS.some((p) => window.location.pathname.startsWith(p))) return
      const live = await deployedBuild()
      if (!stopped && live && live !== mine) setShow(true)
    }

    const timer = window.setInterval(check, CHECK_INTERVAL_MS)
    // Coming back to a tab that has been open for days is exactly when this matters most.
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)

    // Not on first paint — let the page settle before spending a request on this.
    const initial = window.setTimeout(check, 30_000)

    return () => {
      stopped = true
      window.clearInterval(timer)
      window.clearTimeout(initial)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!show) return null

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[min(92vw,520px)]
                 rounded-xl border border-gray-200 bg-white shadow-2xl px-4 py-3
                 flex items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm">A new version of the site is available</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Reload to pick up the latest. Anything you have typed on this page will be lost.
        </p>
      </div>
      <button
        onClick={() => {
          try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)) } catch { /* ignore */ }
          setShow(false)
        }}
        className="text-xs px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 whitespace-nowrap"
      >
        Not now
      </button>
      <button
        onClick={() => window.location.reload()}
        className="text-xs font-semibold px-3 py-2 rounded-lg bg-primary-600 text-white
                   hover:bg-primary-700 whitespace-nowrap"
      >
        Reload
      </button>
    </div>
  )
}
